import { observer } from "mobx-react-lite";
import React, { Fragment, useCallback, } from "react";
import { IconOptions } from "../utils/iconOptions";
import { useFormContext } from "../Form";
import { withFormWrapper } from "../withFormWrapper";
import { StaticMultipleValues } from "../StaticInput";
import { useRadioButtonClasses } from "./RadioButtons.styles";
import { classNames } from "../utils/classNames";
import { Tooltip } from "../Tooltip";
import { useSelectionAreaClasses } from "../styling/SelectionArea.styles";
const RawRadioButtons = observer(({ name, onChange, options, type = "standard", staticFormatter, label, dataId, dataLabel, dataTestId, disabled, uniqueId, }) => {
    const { values, formDisabled, isStatic } = useFormContext();
    const selectedValue = values.get(name);
    const handleChange = useCallback((newValue) => {
        if (onChange) {
            onChange(newValue, selectedValue);
        }
        values.set(name, newValue);
    }, [onChange, values, name, selectedValue]);
    if (isStatic) {
        if (!label)
            console.warn(`${name} requires a label for it to work with isStatic`);
        if (label) {
            return (React.createElement(StaticMultipleValues, { label: label, name: name, staticFormatter: staticFormatter, options: options, dataId: dataId, dataLabel: dataLabel, dataTestId: dataTestId }));
        }
        return React.createElement("div", null);
    }
    return (React.createElement(Container, { type: type }, options.map((option, i) => {
        const id = `${name}${option?.value}${uniqueId ? `-${uniqueId}` : ""}`;
        return (React.createElement(Radio, { key: id, type: type, disabled: option.disabled || formDisabled || disabled, checked: option.value === selectedValue, last: i + 1 === options.length, first: i === 0, uniqueId: id, tooltip: option?.tooltip, name: name, onChange: () => handleChange(option.value), label: option.label, value: option?.value, hasIcon: !!option?.icon }, type === "icon" ? (React.createElement(Content, { label: option.label, icon: option?.icon })) : (option.label)));
    })));
});
const Container = (props) => {
    const classes = useRadioButtonClasses({ type: props?.type });
    const { selectionAreaGroup } = useSelectionAreaClasses({});
    return (React.createElement("div", { className: classNames("radio-buttons", classes.radioButtons), role: "radiogroup" },
        React.createElement("div", { className: classNames(props?.type === "icon" ? selectionAreaGroup : undefined, props?.type === "button" ? classes.radioButtonsGroup : undefined) }, props.children)));
};
const Radio = (props) => {
    const { disabled, checked } = props;
    const classes = useRadioButtonClasses({
        type: props?.type ?? "standard",
    });
    const { selectionLabel } = useSelectionAreaClasses({
        hasIcon: props?.hasIcon,
    });
    return (React.createElement(SelectionArea, { type: props?.type ?? "standard", disabled: disabled, checked: checked, last: props?.last, first: props?.first, uniqueId: props?.uniqueId, tooltip: props?.tooltip, label: props?.label, hasIcon: props?.hasIcon },
        React.createElement("input", { id: props?.uniqueId, className: classNames(classes.input, disabled ? classes.inputDisabled : undefined), onChange: props?.onChange, disabled: disabled, checked: checked, type: "radio", autoComplete: "off" }),
        React.createElement("label", { className: classNames(props?.type === "icon" ? selectionLabel : classes.label, disabled ? classes.labelDisabled : undefined), htmlFor: props?.uniqueId, "data-checked": checked ? "true" : "false", "data-disabled": disabled ? "true" : "false", "data-name": props?.name, "data-value": props?.value }, props?.children)));
};
const SelectionArea = (props) => {
    const { checked, disabled, type } = props;
    const { buttonArea, buttonAreaChecked, buttonAreaDisabled, buttonAreaCheckedDisabled, } = useRadioButtonClasses({
        type: props?.type,
        last: props?.last,
        first: props?.first,
    });
    const { selectionArea, selectionAreaChecked, selectionAreaDisabled, selectionAreaCheckedDisabled, } = useSelectionAreaClasses({
        last: props?.last,
        first: props?.first,
        hasIcon: props?.hasIcon,
    });
    return (React.createElement("div", { "data-checked": checked ? "true" : "false", "data-disabled": disabled ? "true" : "false", "data-label": props?.label, className: classNames(type === "button" ? buttonArea : undefined, type === "icon" ? selectionArea : undefined, type === "button" && checked ? buttonAreaChecked : undefined, type === "icon" && checked ? selectionAreaChecked : undefined, type === "button" && disabled ? buttonAreaDisabled : undefined, type === "icon" && disabled ? selectionAreaDisabled : undefined, type === "button" && checked && disabled
            ? buttonAreaCheckedDisabled
            : undefined, type === "icon" && checked && disabled
            ? selectionAreaCheckedDisabled
            : undefined) }, props?.tooltip ? (React.createElement(Tooltip, { tooltipId: `tooltip_${props?.uniqueId}`, content: props.tooltip, iconShown: false, placement: "bottom" }, props?.children)) : (React.createElement(Fragment, null, props.children))));
};
const Content = (props) => {
    const classes = useRadioButtonClasses({ type: "icon" });
    const iconClassesFromIconOption = (option) => {
        const iconType = option && IconOptions[option].type === "pro"
            ? "glyphicon-pro"
            : "glyphicon";
        return `icon ${iconType} ${iconType}-${IconOptions[option].value}`;
    };
    return (React.createElement("span", { className: classes.richBlock },
        React.createElement("span", { className: classNames(props?.icon && iconClassesFromIconOption(props?.icon), classes.richIcon) }),
        React.createElement("span", { className: classes.richLabel }, props?.label)));
};
export const RadioButtons = withFormWrapper({
    WrappedComponent: RawRadioButtons,
    options: "group",
});
